/**
 * Mapbox ベクタタイルのフィーチャ群 → 点を内包する「建物」「敷地」のリング化。
 *
 * Overpass の代わりに、既にロード済みの Mapbox タイル (composite の building /
 * landuse) から実形状を取り出すための純ロジック。地図インスタンスへの依存は
 * ここには持たせない (プローブ地図の生成・クエリは services/mapbox-footprint.ts
 * が担う)。テスト可能に保つ。
 *
 * タイル境界をまたぐ地物は複数フィーチャに割れるが、mapbox-streets-v8 では**同一
 * feature id** が振られる (実測: 4,454 building 中 97 件が「同 id・別ジオメトリ」=
 * タイル分割)。よって「同 id の全フィーチャを union」で分割を復元できる。
 * 単純な重複 (タイルバッファの重なり) も同じ union で吸収される。
 *
 * 敷地 (landuse) はさらに「複数区画で1つの避難場所」がある (実測: 印旛西部公園 =
 * 同 id 8断片 + 隣接する別 id の park 区画 / 新河岸東公園一帯 = facility 12断片 +
 * 隣接 park + school)。同 id union だけでは区画の一部しか取れないため、
 * **隣接 (交差) する許可クラス区画を連結 union** して「一帯」を復元する。
 * landuse は道路で切れるので、連結は街区で自然に止まる。
 */
import {
  booleanIntersects,
  booleanPointInPolygon,
  featureCollection,
  union,
} from '@turf/turf';
import type { Feature, MultiPolygon, Point, Polygon, Position } from 'geojson';

/** querySourceFeatures が返すフィーチャの必要部分だけ (map 依存を避けるため最小型)。 */
export interface TileBuildingFeature {
  id?: string | number;
  properties?: Record<string, unknown> | null;
  geometry?: { type: string; coordinates: unknown } | null;
}

type Ring = Array<[number, number]>;

/** Polygon/MultiPolygon の GeoJSON Feature に正規化 (それ以外は null)。 */
function asPolyFeature(
  f: TileBuildingFeature,
): Feature<Polygon | MultiPolygon> | null {
  const g = f.geometry;
  if (!g) return null;
  if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') return null;
  return {
    type: 'Feature',
    properties: {},
    geometry: g as Polygon | MultiPolygon,
  };
}

/** 外周リングの符号なし面積 (度²、相対比較用)。 */
function ringArea(feat: Feature<Polygon | MultiPolygon>): number {
  const ring =
    feat.geometry.type === 'Polygon'
      ? feat.geometry.coordinates[0]
      : feat.geometry.coordinates[0]?.[0];
  if (!ring) return 0;
  let s = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j];
    const b = ring[i];
    if (!a || !b) continue;
    const ax = a[0] ?? 0;
    const ay = a[1] ?? 0;
    const bx = b[0] ?? 0;
    const by = b[1] ?? 0;
    s += (ax + bx) * (ay - by);
  }
  return Math.abs(s) / 2;
}

/** リングを閉じる (始点=終点)。 */
function closeRing(r: Ring): Ring {
  if (r.length < 3) return r;
  const a = r[0]!;
  const b = r[r.length - 1]!;
  return a[0] === b[0] && a[1] === b[1] ? r : [...r, [a[0], a[1]]];
}

/** 6桁丸め (~0.1m。保存サイズ削減 + 焼き込みマスターと桁を揃える)。 */
function round6(r: Ring): Ring {
  return r.map(
    ([x, y]) => [Math.round(x * 1e6) / 1e6, Math.round(y * 1e6) / 1e6] as [
      number,
      number,
    ],
  );
}

/** 外周リングを Polygon Feature に包む (booleanPointInPolygon 用)。 */
function ringFeatureOf(ring: Position[]): Feature<Polygon> {
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [ring] },
  };
}

/**
 * geom のうち**点を内包する**サブポリゴンの外周リングを返す (無ければ null)。
 *
 * union 結果が MultiPolygon になる (破片が離れている) ことがある。ここで
 * 「頂点最多」ではなく「点を含む」ポリゴンを選ぶことが肝: 頂点数で選ぶと、点を
 * 含まない別の破片を返して、避難先が実際とズレた建物に描かれてしまう。
 */
function ringContainingPoint(
  geom: Polygon | MultiPolygon,
  pt: Feature<Point>,
): Ring | null {
  const rings: Position[][] =
    geom.type === 'Polygon'
      ? geom.coordinates[0]
        ? [geom.coordinates[0]]
        : []
      : geom.coordinates.map((poly) => poly[0]).filter((r): r is Position[] => !!r);
  for (const r of rings) {
    try {
      if (booleanPointInPolygon(pt, ringFeatureOf(r))) return r as Ring;
    } catch {
      // 不正リングはスキップ
    }
  }
  return null;
}

/**
 * building フィーチャ群から、点 `[lng, lat]` を内包する建物のフットプリントを返す。
 * 取れなければ null (= その点は建物の上に無い → 呼び出し側は OSM/円へフォールバック)。
 *
 * - `underground === 'true'` (文字列) の地下構造物は除外する。
 * - 複数建物が点を含む場合は最小面積 (最も具体的な建物) を種にする。
 * - 種と同一 feature id の全フィーチャを union してタイル分割を復元する。
 * - **返すリングは必ず点を内包する** (union が MultiPolygon 化しても内包片を選ぶ。
 *   保証できなければ種の内包片へフォールバック、それも無理なら null)。
 */
export function buildingRingFromFeatures(
  features: TileBuildingFeature[],
  point: [number, number],
): Ring | null {
  const pt: Feature<Point> = {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Point', coordinates: point },
  };

  const containing: Array<{
    f: TileBuildingFeature;
    feat: Feature<Polygon | MultiPolygon>;
    area: number;
  }> = [];
  for (const f of features) {
    if ((f.properties?.['underground'] as string) === 'true') continue;
    const feat = asPolyFeature(f);
    if (!feat) continue;
    try {
      if (!booleanPointInPolygon(pt, feat)) continue;
    } catch {
      continue;
    }
    containing.push({ f, feat, area: ringArea(feat) });
  }
  if (containing.length === 0) return null;

  // 最も具体的な (最小面積の) 建物を種にする。種は点を内包することが保証済み。
  containing.sort((a, b) => a.area - b.area);
  const seed = containing[0]!;

  // 同一 feature id の全フィーチャ = タイル分割の破片。union で1枚に戻す。
  // 種 (内包が保証された片) から始める = union が失敗しても内包を失わない。
  const id = seed.f.id;
  let acc: Feature<Polygon | MultiPolygon> = seed.feat;
  if (id != null) {
    for (const f of features) {
      if (f.id !== id) continue;
      const frag = asPolyFeature(f);
      if (!frag) continue;
      try {
        const u = union(featureCollection([acc, frag]));
        if (u) acc = u as Feature<Polygon | MultiPolygon>;
      } catch {
        // union 失敗 (数値誤差) → それまでの acc を維持 (種を含むので内包は保たれる)
      }
    }
  }

  // union 結果 (or 種) のうち点を内包する片。取れなければ種の内包片 → それも無理なら null。
  const ring =
    ringContainingPoint(acc.geometry, pt) ??
    ringContainingPoint(seed.feat.geometry, pt);
  if (!ring || ring.length < 3) return null;
  return round6(closeRing(ring));
}

/**
 * 敷地として扱う landuse クラスの許可リスト。
 *
 * residential / commercial_area / industrial は街区〜地区の面で「施設の敷地」では
 * ないため除外。grass も除外 — 道路沿いの緑地帯が区画同士を橋渡しして、連結 union
 * が街区を越えて延伸する事故を防ぐ (実測で敷地ヒットに寄与していたのは
 * park / school / facility のみ)。
 */
export const GROUNDS_CLASSES: ReadonlySet<string> = new Set([
  'park',
  'pitch',
  'school',
  'facility',
  'cemetery',
  'hospital',
  'parking',
]);

/** 連結 union の暴走ガード: 敷地の対角線がこの距離を超える統合はしない (m)。 */
const GROUNDS_MAX_DIAG_M = 2000;

/** Feature 群の bbox。 */
function featBBox(feat: Feature<Polygon | MultiPolygon>): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const polys =
    feat.geometry.type === 'Polygon'
      ? [feat.geometry.coordinates]
      : feat.geometry.coordinates;
  for (const poly of polys) {
    for (const ring of poly) {
      for (const p of ring) {
        const x = p[0] ?? 0;
        const y = p[1] ?? 0;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  return [minX, minY, maxX, maxY];
}

/** bbox の対角線長 (m, 概算)。 */
function bboxDiagM(b: [number, number, number, number]): number {
  const midLat = (b[1] + b[3]) / 2;
  const kx = Math.cos((midLat * Math.PI) / 180) * 111_320;
  return Math.hypot((b[2] - b[0]) * kx, (b[3] - b[1]) * 111_320);
}

/**
 * landuse フィーチャ群から、点 `[lng, lat]` を内包する**敷地**のリングを返す。
 * 取れなければ null (= 点は許可クラスの敷地の上に無い → 建物へフォールバック)。
 *
 * 手順 (印旛西部公園・新河岸東公園一帯の実測から設計):
 *   1. 点を内包する許可クラス区画のうち**最大**を種にする (pitch より park 全体)
 *   2. 種と同 id の断片を union (タイル分割の復元)
 *   3. **交差/隣接する許可クラス区画を連結 union** (複数区画の「一帯」を復元)。
 *      landuse は道路で切れるため、連結は街区で自然に止まる。対角 2km を超える
 *      統合はしない (暴走ガード)。
 *   4. 返すリングは必ず点を内包する (建物と同じ保証)。
 */
export function groundsRingFromFeatures(
  features: TileBuildingFeature[],
  point: [number, number],
  opts: {
    /**
     * 連結 union の暴走ガード (対角, m)。既定 2000 = 実行時プローブの挙動。
     * ベイク/修復側は「既知の敷地サイズ」が分かっているとき、それに応じて広げる
     * (実測: 酪農学園 2.4km・水元公園 3.4km 等の大型敷地は 2km で打ち切られ、
     * 敷地の一部しか統合されなかった)。
     */
    maxDiagM?: number;
  } = {},
): Ring | null {
  const maxDiagM = opts.maxDiagM ?? GROUNDS_MAX_DIAG_M;
  const pt: Feature<Point> = {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Point', coordinates: point },
  };

  const frags: Array<{
    feat: Feature<Polygon | MultiPolygon>;
    id: string | number | undefined;
    area: number;
  }> = [];
  for (const f of features) {
    const cls = String(f.properties?.['class'] ?? '');
    if (!GROUNDS_CLASSES.has(cls)) continue;
    const feat = asPolyFeature(f);
    if (!feat) continue;
    frags.push({ feat, id: f.id, area: ringArea(feat) });
  }
  if (frags.length === 0) return null;

  const containing = frags.filter((x) => {
    try {
      return booleanPointInPolygon(pt, x.feat);
    } catch {
      return false;
    }
  });
  if (containing.length === 0) return null;

  // 敷地は「最大の内包区画」を種にする (建物の最小と逆 — pitch より park 全体が site)。
  containing.sort((a, b) => b.area - a.area);
  const seed = containing[0]!;

  // 同 id union → 連結 union。種から始める = 内包の保証を失わない。
  let acc: Feature<Polygon | MultiPolygon> = seed.feat;
  if (seed.id != null) {
    for (const x of frags) {
      if (x.id !== seed.id || x.feat === seed.feat) continue;
      try {
        const u = union(featureCollection([acc, x.feat]));
        if (u) acc = u as Feature<Polygon | MultiPolygon>;
      } catch {
        /* union 失敗 → acc 維持 */
      }
    }
  }

  // 連結 union (flood-fill): acc に触れる許可クラス区画を統合し尽くす。
  const used = new Set<number>();
  frags.forEach((x, i) => {
    if (seed.id != null && x.id === seed.id) used.add(i);
  });
  let changed = true;
  let guard = 0;
  while (changed && guard++ < 10) {
    changed = false;
    for (let i = 0; i < frags.length; i++) {
      if (used.has(i)) continue;
      const x = frags[i]!;
      let touches = false;
      try {
        touches = booleanIntersects(acc, x.feat);
      } catch {
        used.add(i);
        continue;
      }
      if (!touches) continue;
      used.add(i);
      // 暴走ガード: 統合で敷地が対角 2km を超えるならその区画は取り込まない
      const ab = featBBox(acc);
      const xb = featBBox(x.feat);
      const merged: [number, number, number, number] = [
        Math.min(ab[0], xb[0]),
        Math.min(ab[1], xb[1]),
        Math.max(ab[2], xb[2]),
        Math.max(ab[3], xb[3]),
      ];
      if (bboxDiagM(merged) > maxDiagM) continue;
      try {
        const u = union(featureCollection([acc, x.feat]));
        if (u) {
          acc = u as Feature<Polygon | MultiPolygon>;
          changed = true;
        }
      } catch {
        /* union 失敗 → acc 維持 */
      }
    }
  }

  const ring =
    ringContainingPoint(acc.geometry, pt) ??
    ringContainingPoint(seed.feat.geometry, pt);
  if (!ring || ring.length < 3) return null;
  return round6(closeRing(ring));
}

/**
 * 点からリング境界までの最短距離 (m, 概算)。GSI 建物の「近傍救済」判定用。
 * 頂点間の辺への垂線距離も見る (長い壁の途中に点があるケースを取りこぼさない)。
 */
export function pointToRingDistanceM(
  point: [number, number],
  ring: Ring,
): number {
  const kx = Math.cos((point[1] * Math.PI) / 180) * 111_320;
  const ky = 111_320;
  const px = point[0] * kx;
  const py = point[1] * ky;
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j]!;
    const b = ring[i]!;
    const ax = a[0]! * kx;
    const ay = a[1]! * ky;
    const bx = b[0]! * kx;
    const by = b[1]! * ky;
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const d = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
    if (d < best) best = d;
  }
  return best;
}
